import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { TypeSpeakExercise } from '../../mockLessonContent';
import { BLUE, cardSx, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT, WARNING_TEXT, YELLOW, yellowButtonSx } from '../../studentStyles';
import AudioPlayButton from '../AudioPlayButton';
import RecordAnswerPanel from './RecordAnswerPanel';
import SpeakingHero from './SpeakingHero';

interface TypeSpeakExerciseCardProps {
    exercise: TypeSpeakExercise;
    onReady: () => void;
}

// "Prompt, Type, Speak" (and its V2). The typed answer goes through a check that
// either accepts it or hands back a correction; the sentence is spoken after.
export default function TypeSpeakExerciseCard({ exercise, onReady }: TypeSpeakExerciseCardProps) {
    const [answer, setAnswer] = React.useState('');
    const [checked, setChecked] = React.useState(false);

    // The mock accepts the suggested sentence as-is and corrects anything else —
    // the real check is an LLM call returning CorrectedResponse.
    const isAccepted = checked && answer.trim() === exercise.suggestion.hanzi;

    return (
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <SpeakingHero label="TYPE, THEN SAY" hanzi={exercise.chineseHanzi} pinyin={exercise.chinesePinyin} />

            <Box sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '14px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${BLUE}1A`,
                    }}>
                        <VolumeUpRoundedIcon sx={{ color: BLUE }} />
                    </Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, flex: 1, minWidth: 0 }}>
                        {exercise.english}
                    </Typography>
                    <AudioPlayButton ariaLabel="Play the question" size={40} />
                </Stack>

                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 2.5 }}>
                    {exercise.instruction}
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    value={answer}
                    onChange={event => { setAnswer(event.target.value); setChecked(false); }}
                    placeholder="在这里写你的回答……"
                    sx={{
                        mt: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '16px',
                            backgroundColor: SURFACE_SOFT,
                            fontFamily: POPPINS,
                            fontSize: 18,
                            color: NAVY,
                            '& fieldset': { borderColor: RING_TRACK },
                        },
                    }}
                />
                {!checked && (
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={answer.trim().length === 0}
                        onClick={() => setChecked(true)}
                        sx={{ ...yellowButtonSx, py: 1.25, mt: 1.5 }}
                    >
                        Check my answer
                    </Button>
                )}

                {checked && (
                    <>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ mt: 2, p: 1.75, borderRadius: '14px', backgroundColor: isAccepted ? `${GREEN}14` : `${YELLOW}26` }}
                        >
                            {isAccepted
                                ? <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 20, flexShrink: 0 }} />
                                : <AutoFixHighRoundedIcon sx={{ color: WARNING_TEXT, fontSize: 20, flexShrink: 0 }} />}
                            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: NAVY }}>
                                {isAccepted ? '对了! That works — now say it out loud.' : exercise.correctionNote}
                            </Typography>
                        </Stack>

                        <Box sx={{ mt: 1.5, p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 19, color: NAVY, lineHeight: 1.4 }}>
                                    {exercise.suggestion.hanzi}
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                    {exercise.suggestion.pinyin}
                                </Typography>
                            </Box>
                            <AudioPlayButton ariaLabel="Play the model answer" size={40} />
                        </Box>

                        <RecordAnswerPanel grades={exercise.grades} onScored={onReady} />
                    </>
                )}
            </Box>
        </Box>
    );
}
